import React, { useState } from 'react';
import { useBalanceSnapshot } from '../hooks/useBalanceSnapshot';
import { exportToExcel, getTimestampForFilename } from '@/shared/utils/excelExport';
import { format } from 'date-fns';
import { usePagination } from '@/shared/hooks/usePagination';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import '../styles/BalanceSnapshotPage.css';

const BalanceSnapshotPage: React.FC = () => {
    const { snapshots, loading, fetchSnapshots } = useBalanceSnapshot();
    const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [filterTarget, setFilterTarget] = useState<string>('전체');
    const [searchName, setSearchName] = useState<string>('');

    const handleSearch = () => {
        const dateStr = selectedDate.replace(/-/g, '');
        fetchSnapshots(dateStr);
    };

    const handleFilterTargetChange = (value: string) => {
        setFilterTarget(value);
        setSearchName('');
        resetPagination();
    };

    const handleSearchNameChange = (value: string) => {
        setSearchName(value);
        resetPagination();
    };

    const handleExcelDownload = () => {
        if (snapshots.length === 0) return;
        exportToExcel(
            snapshots,
            [
                { header: '협력사명', key: 'supplyName' },
                { header: '라이더명', key: 'riderName' },
                { header: '보유잔액', key: 'balAmt' },
            ],
            `잔액스냅샷_${selectedDate.replace(/-/g, '')}_${getTimestampForFilename()}`
        );
    };

    const filteredSnapshots = snapshots.filter(s => {
        if (filterTarget !== '전체' && s.acctTargetName !== filterTarget) return false;
        if (searchName.trim()) {
            const keyword = searchName.trim();
            return (s.supplyName ?? '').includes(keyword) || (s.riderName ?? '').includes(keyword);
        }
        return true;
    });

    const { currentPage, totalPages, currentItems, goToPage, resetPagination } = usePagination({
        items: filteredSnapshots,
        itemsPerPage: 15,
    });

    return (
        <div className="balance-snapshot-page">
            <div className="page-header">
                <h2>잔액 스냅샷</h2>
            </div>

            {/* 검색 필터 */}
            <div className="filter-card">
                <label>기준일자</label>
                <input
                    type="date"
                    value={selectedDate}
                    onChange={e => setSelectedDate(e.target.value)}
                />
                <label>구분</label>
                <select value={filterTarget} onChange={e => handleFilterTargetChange(e.target.value)}>
                    {['전체', '라이더', '협력사', '본사'].map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                    ))}
                </select>
                <input
                    type="text"
                    className="filter-name-input"
                    placeholder="협력사명 / 라이더명 검색"
                    value={searchName}
                    onChange={e => handleSearchNameChange(e.target.value)}
                />
                <button className="btn btn-primary" onClick={handleSearch} disabled={loading}>
                    조회
                </button>
                <button
                    className="btn btn-success"
                    onClick={handleExcelDownload}
                    disabled={snapshots.length === 0}
                >
                    <i className="fas fa-file-excel"></i>
                    엑셀 다운로드
                </button>
            </div>

            {/* 결과 건수 */}
            <div className="result-bar">
                <i className="fas fa-list" />
                총 <strong>{filteredSnapshots.length.toLocaleString()}건</strong>
            </div>

            {/* 테이블 */}
            <div className="table-container">
                <table className="data-table">
                    <thead>
                    <tr>
                        <th>구분</th>
                        <th>협력사명</th>
                        <th>라이더명</th>
                        <th className="text-right">보유잔액(원)</th>
                        <th className="text-center">기준일자</th>
                    </tr>
                    </thead>
                    <tbody>
                    {loading ? (
                        <tr>
                            <td colSpan={5} className="text-center">로딩 중...</td>
                        </tr>
                    ) : filteredSnapshots.length === 0 ? (
                        <tr>
                            <td colSpan={5} className="text-center">데이터가 없습니다.</td>
                        </tr>
                    ) : (
                        currentItems.map(row => (
                            <tr key={row.seqNo}>
                                <td>{row.acctTargetName}</td>
                                <td>{row.supplyName || '-'}</td>
                                <td>{row.riderName || '-'}</td>
                                <td className="text-right">{row.balAmt.toLocaleString()}</td>
                                <td className="text-center">{row.snapDate}</td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>

            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={goToPage}
            />
        </div>
    );
};

export default BalanceSnapshotPage;
